import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { 
  EventChip, 
  ServiceIcon, 
  PendingAlertSVG, 
  SuccessCheckSVG 
} from './calendarHelpers';

interface ShiftDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDay: Date;
  selectedDayCutoffs: any[];
  isSaving: boolean;
  isEditor: boolean;
  l1Projected: number;
  l1Real: number;
  setL1Real: (val: number) => void;
  l3Projected: number;
  l3Real: number;
  setL3Real: (val: number) => void;
  l1PlannedEvents: string[];
  l1RealEvents: string[];
  setL1RealEvents: (evs: string[]) => void;
  l3PlannedEvents: string[];
  l3RealEvents: string[];
  setL3RealEvents: (evs: string[]) => void;
  onSaveProduction: () => Promise<void>;
  onConfirmCutoff: (cutoff: any, actualStart: string, actualEnd: string, comment: string) => Promise<void>;
  onCancelConfirmation: (cutoff: any) => Promise<void>;
}

export const ShiftDetailModal: React.FC<ShiftDetailModalProps> = ({
  isOpen,
  onClose,
  selectedDay,
  selectedDayCutoffs,
  isSaving,
  isEditor,
  l1Projected,
  l1Real,
  setL1Real,
  l3Projected,
  l3Real,
  setL3Real,
  l1PlannedEvents,
  l1RealEvents,
  setL1RealEvents,
  l3PlannedEvents,
  l3RealEvents,
  setL3RealEvents,
  onSaveProduction,
  onConfirmCutoff,
  onCancelConfirmation
}) => {
  const [confirmingCutoffId, setConfirmingCutoffId] = useState<string | null>(null);
  const [actualStart, setActualStart] = useState('');
  const [actualEnd, setActualEnd] = useState('');
  const [operatorComment, setOperatorComment] = useState('');

  if (!isOpen) return null;

  const handleToggleEvent = (line: 'L1' | 'L3', eventName: string) => {
    const list = line === 'L1' ? l1RealEvents : l3RealEvents;
    const setter = line === 'L1' ? setL1RealEvents : setL3RealEvents;
    if (list.includes(eventName)) {
      setter(list.filter(e => e !== eventName));
    } else {
      setter([...list, eventName]);
    }
  };

  const handleConfirmClick = async (cut: any) => {
    await onConfirmCutoff(cut, actualStart, actualEnd, operatorComment);
    setConfirmingCutoffId(null);
    setOperatorComment('');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto border border-gray-100 animate-in zoom-in-95 duration-200 flex flex-col">
        
        {/* Cabecera del Modal */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10 flex-shrink-0">
          <div>
            <span className="text-[10px] font-black uppercase tracking-widest text-[#d1a228] bg-amber-50 px-2.5 py-1 rounded-full">
              Ficha Operativa de Turno
            </span>
            <h3 className="text-xl font-black text-gray-900 mt-2">
              {selectedDay.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </h3>
          </div>
          <button 
            onClick={onClose} 
            disabled={isSaving} 
            className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-xl transition-all"
          >
            ✕
          </button>
        </div>

        {/* Contenido Modular Split: Izquierda (Envasado) vs Derecha (Checklist Cortes) */}
        <div className="p-6 overflow-y-auto flex-1 grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* COLUMNA IZQUIERDA: PLAN DE ENVASADO Y EVENTOS */}
          <div className="space-y-6">
            <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
              Líneas de Envasado & Operación Real
            </h4>

            {/* SECCIÓN L1 */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-4 shadow-sm">
              <div className="flex justify-between items-center">
                <span className="text-[13px] font-black text-slate-800 uppercase tracking-wider">Línea L1 (Cerveza)</span>
                <span className="text-[10px] bg-slate-200/80 px-2.5 py-0.5 rounded-full font-bold text-slate-500">
                  Plan: {l1Projected} hl
                </span>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div>
                  <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Volumen Envasado Real (hl)</label>
                  <input 
                    type="number" 
                    value={l1Real || ''} 
                    onChange={(e) => setL1Real(Number(e.target.value))}
                    className="w-full text-xs font-semibold p-2.5 bg-white rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#E2B53F]"
                    placeholder="hl reales..."
                  />
                </div>
              </div>

              {/* Eventos L1 */}
              <div>
                <label className="block text-[9px] font-bold text-slate-400 uppercase mb-2">Eventos Reales Ocurridos</label>
                <div className="flex flex-wrap gap-2">
                  {(['CIP', 'Mantenimiento', 'Cambio Formato'] as const).map(name => (
                    <EventChip 
                      key={`l1-real-chip-${name}`}
                      name={name}
                      isActive={l1RealEvents.includes(name)}
                      onClick={() => handleToggleEvent('L1', name)}
                      type="real"
                    />
                  ))}
                </div>
                {l1PlannedEvents.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[9px] text-slate-400 font-extrabold uppercase">Planificado:</span>
                    {l1PlannedEvents.map(e => (
                      <span key={`l1-plan-badge-${e}`} className="text-[9px] bg-amber-50 border border-amber-200 text-[#d1a228] px-2 py-0.5 rounded-full font-black uppercase">
                        {e}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SECCIÓN L3 */}
            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-4 shadow-sm">
              <div className="flex justify-between items-center">
                <span className="text-[13px] font-black text-slate-800 uppercase tracking-wider">Línea L3 (Cerveza)</span>
                <span className="text-[10px] bg-slate-200/80 px-2.5 py-0.5 rounded-full font-bold text-slate-500">
                  Plan: {l3Projected} hl
                </span>
              </div>

              <div className="grid grid-cols-1 gap-2">
                <div>
                  <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Volumen Envasado Real (hl)</label>
                  <input 
                    type="number" 
                    value={l3Real || ''} 
                    onChange={(e) => setL3Real(Number(e.target.value))}
                    className="w-full text-xs font-semibold p-2.5 bg-white rounded-xl border border-slate-200 focus:ring-2 focus:ring-[#E2B53F]"
                    placeholder="hl reales..."
                  />
                </div>
              </div>

              {/* Eventos L3 */}
              <div>
                <label className="block text-[9px] font-bold text-slate-400 uppercase mb-2">Eventos Reales Ocurridos</label>
                <div className="flex flex-wrap gap-2">
                  {(['CIP', 'Mantenimiento', 'Cambio Formato'] as const).map(name => (
                    <EventChip 
                      key={`l3-real-chip-${name}`}
                      name={name}
                      isActive={l3RealEvents.includes(name)}
                      onClick={() => handleToggleEvent('L3', name)}
                      type="real"
                    />
                  ))}
                </div>
                {l3PlannedEvents.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[9px] text-slate-400 font-extrabold uppercase">Planificado:</span>
                    {l3PlannedEvents.map(e => (
                      <span key={`l3-plan-badge-${e}`} className="text-[9px] bg-amber-50 border border-amber-200 text-[#d1a228] px-2 py-0.5 rounded-full font-black uppercase">
                        {e}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Guardar Plan de Envasado */}
            <button 
              onClick={onSaveProduction}
              disabled={isSaving}
              className="w-full py-3 bg-zinc-950 hover:bg-zinc-900 text-white rounded-2xl text-xs font-black uppercase tracking-wider transition-all shadow-md hover:shadow-lg disabled:opacity-50"
            >
              {isSaving ? 'Guardando...' : 'Guardar Producción Real'}
            </button>
          </div>

          {/* COLUMNA DERECHA: CHECKLIST DE CORTES DE SERVICIOS */}
          <div className="space-y-6">
            <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider border-b border-slate-100 pb-2">
              Cortes de Suministro Programados
            </h4>

            {/* Checklist Principal */}
            <div className="space-y-3">
              {selectedDayCutoffs.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
                  <p className="text-xs text-slate-400 font-black">No hay cortes de servicios planificados para hoy.</p>
                </div>
              ) : (
                selectedDayCutoffs.map(cut => {
                  const isThisConfirming = confirmingCutoffId === cut.id;
                  
                  return (
                    <div 
                      key={cut.id} 
                      className={`border border-l-4 rounded-xl p-3.5 bg-white transition-all shadow-sm ${
                        cut.isConfirmed 
                          ? 'border-slate-200 border-l-emerald-500' 
                          : 'border-slate-200 border-l-rose-500 bg-rose-50/5'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className={`p-1.5 rounded-lg ${
                            cut.isConfirmed 
                              ? 'bg-emerald-50 text-emerald-600' 
                              : 'bg-rose-50 text-rose-600'
                          }`}>
                            <ServiceIcon type={cut.serviceType} className="h-4 w-4" />
                          </div>
                          <div>
                            <h5 className="text-[11px] font-black text-slate-900 uppercase tracking-wider">
                              {cut.serviceType === 'electricity' ? 'Electricidad' : cut.serviceType === 'water' ? 'Agua' : cut.serviceType === 'steam' ? 'Vapor' : 'CO2'} - {cut.lineId} {cut.cutoffReason ? `(${cut.cutoffReason})` : ''}
                            </h5>
                            <p className="text-[10px] text-slate-500 font-bold flex items-center gap-1 mt-0.5">
                              <span className="text-slate-400">Plan:</span>
                              <span className="font-extrabold text-slate-700">{cut.plannedStartTime} - {cut.plannedEndTime}</span>
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {!cut.isConfirmed ? (
                            <>
                              {!isThisConfirming ? (
                                <button 
                                  onClick={() => {
                                    setConfirmingCutoffId(cut.id || null);
                                    setActualStart(cut.plannedStartTime);
                                    setActualEnd(new Date().toTimeString().substring(0, 5));
                                  }}
                                  className="px-2.5 py-1.5 bg-zinc-950 hover:bg-zinc-900 text-white font-black rounded-lg text-[9px] uppercase tracking-wider transition-all flex items-center gap-1 shadow-sm"
                                >
                                  Atender Corte
                                </button>
                              ) : (
                                <button onClick={() => setConfirmingCutoffId(null)} className="p-1 hover:bg-slate-100 rounded-lg"><XMarkIcon className="h-4 w-4 text-slate-500" /></button>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <span className="text-[9px] text-emerald-800 bg-emerald-50 border border-emerald-250 px-2 py-0.5 rounded-full font-black uppercase tracking-wider flex items-center gap-1 shadow-sm">
                                <SuccessCheckSVG className="h-3 w-3" /> Cerrado
                              </span>
                              <button onClick={() => onCancelConfirmation(cut)} className="p-1 text-slate-400 hover:text-slate-600 transition-colors" title="Revertir cierre">
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Subformulario inline de Cierre */}
                      {isThisConfirming && (
                        <div className="mt-3 p-3 bg-white border border-slate-150 rounded-xl space-y-3 shadow-inner">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="block text-[9px] font-bold text-slate-400 uppercase">Hora Real Cierre</label>
                                <button 
                                  type="button"
                                  onClick={() => setActualStart(new Date().toTimeString().substring(0, 5))}
                                  className="text-[8px] font-extrabold text-[#E2B53F] hover:underline"
                                >
                                  Hora Actual
                                </button>
                              </div>
                              <input type="time" value={actualStart} onChange={(e) => setActualStart(e.target.value)} className="w-full p-2 bg-slate-50 rounded-xl border border-slate-200 text-xs focus:ring-1 focus:ring-[#E2B53F]" />
                            </div>
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <label className="block text-[9px] font-bold text-slate-400 uppercase">Hora Real Apertura</label>
                                <button 
                                  type="button"
                                  onClick={() => setActualEnd(new Date().toTimeString().substring(0, 5))}
                                  className="text-[8px] font-extrabold text-[#E2B53F] hover:underline"
                                >
                                  Hora Actual
                                </button>
                              </div>
                              <input type="time" value={actualEnd} onChange={(e) => setActualEnd(e.target.value)} className="w-full p-2 bg-slate-50 rounded-xl border border-slate-200 text-xs focus:ring-1 focus:ring-[#E2B53F]" />
                            </div>
                          </div>
                          <div>
                              <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1">Comentario del Corte</label>
                              <textarea 
                                value={operatorComment}
                                onChange={(e) => setOperatorComment(e.target.value)}
                                placeholder="Detalles sobre por qué se cerró antes o incidencias..."
                                className="w-full p-2 bg-slate-50 rounded-xl border border-slate-200 text-xs h-14 focus:ring-1 focus:ring-[#E2B53F]"
                              />
                          </div>
                          <div className="flex justify-end space-x-2">
                            <button onClick={() => setConfirmingCutoffId(null)} className="px-3 py-1 text-xs text-slate-500 font-bold border border-slate-200 rounded-lg">Cancelar</button>
                            <button onClick={() => handleConfirmClick(cut)} className="px-4 py-1 bg-[#E2B53F] hover:bg-[#c99f30] text-gray-900 font-black rounded-lg shadow-sm">Confirmar Cierre</button>
                          </div>
                        </div>
                      )}

                      {/* Info Cerrado */}
                      {cut.isConfirmed && (
                        <div className="mt-2 text-[11px] text-slate-600 bg-white/50 p-2.5 rounded-lg border border-emerald-100">
                          <p className="font-semibold">
                            Corte: <span className="font-black text-slate-800">{cut.actualStartTime} - {cut.actualEndTime}</span> 
                            <span className="text-slate-400 text-[10px] ml-1">por {cut.confirmedBy}</span>
                          </p>
                          {cut.operatorComment && (
                            <p className="mt-1 text-slate-500 italic">"{cut.operatorComment}"</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
